import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from 'react'
import { ZoningType, Zoning } from '@popety-io/types'
import { getSectionLayers, hasDynamicLayers, getLayerConfig } from '../config'

/**
 * Section data that can be passed to the map for visualization
 */
export interface MapSectionData {
  /** Listings data (for purchase_listings, rental_listings sections) */
  listings?: any[]
  /** Transactions data (for transactions section) */
  transactions?: any[]
  /** Amenities data (for amenities section) */
  amenities?: any
  /** Transport data (for access section) */
  transport?: {
    /** Public transport stops (bus, tram, train) */
    publicTransportStops?: any[]
    /** Travel time isochrones (walking, cycling, driving) */
    isochrones?: {
      walking?: any
      cycling?: any
      driving?: any
    }
    /** Active mobility metrics (EV charging stations) */
    activeMobilityMetrics?: {
      evStationsNearby?: any[]
    }
    /** Road access metrics (highway access points) */
    roadAccessMetrics?: {
      allHighwayAccess?: any[]
    }
    /** Transport services helper */
    services?: any
  }
  /** Zoning data (for zoning section) */
  zoningData?: {
    general?: Zoning[]
    cantonal?: Zoning[]
    local?: Zoning[]
    partial?: Zoning[]
  }
  /** Generic data for custom sections */
  [key: string]: any
}

/**
 * Layer state for dynamic layers
 */
export interface DynamicLayerState {
  /** Whether the layer is enabled/visible */
  enabled: boolean
  /** Layer opacity (0-100) */
  opacity: number
  /** Whether the layer is currently loading data */
  loading?: boolean
  /** Error message if layer failed to load */
  error?: string
}

/**
 * Map Section Context Value
 */
export interface MapSectionContextValue {
  /** Currently active section ID */
  activeSection: string | null
  /** Set the active section */
  setActiveSection: (sectionId: string | null) => void

  /** Data for the current section */
  sectionData: MapSectionData | null
  /** Update section data */
  setSectionData: (data: MapSectionData | null) => void

  /** State of all dynamic layers */
  layerStates: Record<string, DynamicLayerState>
  /** Toggle a layer on/off */
  toggleLayer: (layerId: string, enabled: boolean) => void
  /** Set layer opacity */
  setLayerOpacity: (layerId: string, opacity: number) => void

  /** Get layer IDs for the active section */
  getActiveSectionLayers: () => string[]
  /** Check if current section has dynamic layers */
  hasActiveSectionLayers: () => boolean

  /** Interaction callbacks */
  onMarkerClick?: (item: any) => void
  onMarkerHover?: (item: any | null) => void
  setOnMarkerClick: (callback: ((item: any) => void) | undefined) => void
  setOnMarkerHover: (callback: ((item: any | null) => void) | undefined) => void

  /** Zoning-specific state */
  selectedZoningType: 'all' | ZoningType
  setSelectedZoningType: (type: 'all' | ZoningType) => void
  isSatelliteZoning: boolean
  toggleSatelliteZoning: () => void
  /** Zoning types with zero count (disabled) */
  disabledZoningTypes: ZoningType[]
  setDisabledZoningTypes: (types: ZoningType[]) => void
}

export const MapSectionContext = createContext<
  MapSectionContextValue | undefined
>(undefined)

export interface MapSectionProviderProps {
  children: ReactNode
  initialSection?: string | null
  initialData?: MapSectionData | null
}

/**
 * Provider for Map Section Context
 *
 * Manages the active section, section data, and dynamic layer states.
 */
export const MapSectionProvider: React.FC<MapSectionProviderProps> = ({
  children,
  initialSection = null,
  initialData = null,
}) => {
  const [activeSection, setActiveSectionState] = useState<string | null>(
    initialSection
  )
  const [sectionData, setSectionDataState] = useState<MapSectionData | null>(
    initialData
  )
  const activeSectionRef = useRef(initialSection)
  const [layerStates, setLayerStates] = useState<
    Record<string, DynamicLayerState>
  >(() => {
    // Initialize layer states on mount if initialSection is provided
    if (initialSection && hasDynamicLayers(initialSection)) {
      const layerIds = getSectionLayers(initialSection)
      const initialStates: Record<string, DynamicLayerState> = {}

      layerIds.forEach((layerId) => {
        const storageKey = `map-layer-${layerId}`
        const stored = localStorage.getItem(storageKey)

        if (stored) {
          try {
            initialStates[layerId] = JSON.parse(stored)
          } catch {
            const layerConfig = getLayerConfig(layerId)
            initialStates[layerId] = {
              enabled: layerConfig?.defaultEnabled ?? true,
              opacity: layerConfig?.defaultOpacity ?? 100,
            }
          }
        } else {
          const layerConfig = getLayerConfig(layerId)
          initialStates[layerId] = {
            enabled: layerConfig?.defaultEnabled ?? true,
            opacity: layerConfig?.defaultOpacity ?? 100,
          }
        }
      })

      return initialStates
    }
    return {}
  })

  // Interaction callbacks
  const [onMarkerClick, setOnMarkerClick] = useState<
    ((item: any) => void) | undefined
  >()
  const [onMarkerHover, setOnMarkerHover] = useState<
    ((item: any | null) => void) | undefined
  >()

  // Zoning-specific state (with localStorage persistence)
  const [selectedZoningType, setSelectedZoningTypeState] = useState<
    'all' | ZoningType
  >(() => {
    const stored = localStorage.getItem('map-selected-zoning-type')
    return (stored as 'all' | ZoningType) || 'all'
  })

  const [isSatelliteZoning, setIsSatelliteZoning] = useState<boolean>(() => {
    const stored = localStorage.getItem('map-is-satellite-zoning')
    return stored === 'true'
  })

  const [disabledZoningTypes, setDisabledZoningTypes] = useState<ZoningType[]>(
    []
  )

  /**
   * Auto-calculate disabled zoning types from zoning data
   */
  useEffect(() => {
    const zoningData = sectionData?.zoningData
    if (!zoningData) {
      // No zoning data - all types disabled
      setDisabledZoningTypes(['general', 'cantonal', 'local', 'partial'])
      return
    }

    const disabled: ZoningType[] = []
    const types: ZoningType[] = ['general', 'cantonal', 'local', 'partial']

    types.forEach((type) => {
      const zones = zoningData[type]
      if (!zones || zones.length === 0) {
        disabled.push(type)
      }
    })

    setDisabledZoningTypes(disabled)
  }, [sectionData?.zoningData])

  /**
   * Auto-reset selected zoning type to 'all' if it becomes disabled
   */
  useEffect(() => {
    if (
      selectedZoningType !== 'all' &&
      disabledZoningTypes.includes(selectedZoningType as ZoningType)
    ) {
      setSelectedZoningTypeState('all')
      localStorage.setItem('map-selected-zoning-type', 'all')
    }
  }, [disabledZoningTypes, selectedZoningType])

  /**
   * Set active section and initialize layer states
   */
  const setActiveSection = useCallback((sectionId: string | null) => {
    const sectionChanged = activeSectionRef.current !== sectionId
    activeSectionRef.current = sectionId

    setActiveSectionState(sectionId)

    if (sectionChanged) {
      setSectionDataState(null)
    }

    // Initialize layer states for the new section
    if (sectionId && hasDynamicLayers(sectionId)) {
      const layerIds = getSectionLayers(sectionId)
      const newStates: Record<string, DynamicLayerState> = {}

      // Load from localStorage or use defaults
      layerIds.forEach((layerId) => {
        const storageKey = `map-layer-${layerId}`
        const stored = localStorage.getItem(storageKey)

        if (stored) {
          try {
            newStates[layerId] = JSON.parse(stored)
          } catch {
            // Use defaults if parse fails from layer config
            const layerConfig = getLayerConfig(layerId)
            newStates[layerId] = {
              enabled: layerConfig?.defaultEnabled ?? true,
              opacity: layerConfig?.defaultOpacity ?? 100,
            }
          }
        } else {
          // Default state from layer config
          const layerConfig = getLayerConfig(layerId)
          newStates[layerId] = {
            enabled: layerConfig?.defaultEnabled ?? true,
            opacity: layerConfig?.defaultOpacity ?? 100,
          }
        }
      })

      setLayerStates(newStates)
    } else {
      // Clear layer states and section data when no section or no layers
      setLayerStates({})
      setSectionDataState(null)
    }
  }, [])

  /**
   * Set section data
   */
  const setSectionData = useCallback((data: MapSectionData | null) => {
    setSectionDataState(data)
  }, [])

  /**
   * Toggle layer visibility
   */
  const toggleLayer = useCallback((layerId: string, enabled: boolean) => {
    setLayerStates((prev) => {
      const newState = { ...prev }

      // Handle exclusive groups (e.g., mutually exclusive isochrones)
      if (enabled) {
        const layerConfig = getLayerConfig(layerId)
        const exclusiveGroup = layerConfig?.exclusiveGroup

        if (exclusiveGroup) {
          // Disable all other layers in the same exclusive group
          Object.keys(newState).forEach((otherLayerId) => {
            if (otherLayerId !== layerId) {
              const otherConfig = getLayerConfig(otherLayerId)
              if (otherConfig?.exclusiveGroup === exclusiveGroup) {
                newState[otherLayerId] = {
                  ...newState[otherLayerId],
                  enabled: false,
                }
                // Persist to localStorage
                localStorage.setItem(
                  `map-layer-${otherLayerId}`,
                  JSON.stringify(newState[otherLayerId])
                )
              }
            }
          })
        }
      }

      // Update the requested layer
      newState[layerId] = {
        ...newState[layerId],
        enabled,
      }

      // Persist to localStorage
      localStorage.setItem(
        `map-layer-${layerId}`,
        JSON.stringify(newState[layerId])
      )

      return newState
    })
  }, [])

  /**
   * Set layer opacity
   */
  const setLayerOpacity = useCallback((layerId: string, opacity: number) => {
    setLayerStates((prev) => {
      const newState = {
        ...prev,
        [layerId]: {
          ...prev[layerId],
          opacity,
        },
      }

      // Persist to localStorage
      localStorage.setItem(
        `map-layer-${layerId}`,
        JSON.stringify(newState[layerId])
      )

      return newState
    })
  }, [])

  /**
   * Set selected zoning type (with localStorage persistence)
   */
  const setSelectedZoningType = useCallback((type: 'all' | ZoningType) => {
    setSelectedZoningTypeState(type)
    localStorage.setItem('map-selected-zoning-type', type)
  }, [])

  /**
   * Toggle satellite mode for zoning (with localStorage persistence)
   */
  const toggleSatelliteZoning = useCallback(() => {
    setIsSatelliteZoning((prev) => {
      const newValue = !prev
      localStorage.setItem('map-is-satellite-zoning', String(newValue))
      return newValue
    })
  }, [])

  /**
   * Get layer IDs for the active section
   */
  const getActiveSectionLayers = useCallback(() => {
    if (!activeSection) return []
    return getSectionLayers(activeSection)
  }, [activeSection])

  /**
   * Check if current section has dynamic layers
   */
  const hasActiveSectionLayers = useCallback(() => {
    if (!activeSection) return false
    // For restrictions section, return true if section is active
    // (we can't check data without creating a circular dependency)
    if (activeSection === 'restrictions' || activeSection === 'hazards') {
      return true
    }
    return hasDynamicLayers(activeSection)
  }, [activeSection])

  const value = useMemo<MapSectionContextValue>(
    () => ({
      activeSection,
      setActiveSection,
      sectionData,
      setSectionData,
      layerStates,
      toggleLayer,
      setLayerOpacity,
      getActiveSectionLayers,
      hasActiveSectionLayers,
      onMarkerClick,
      onMarkerHover,
      setOnMarkerClick,
      setOnMarkerHover,
      selectedZoningType,
      setSelectedZoningType,
      isSatelliteZoning,
      toggleSatelliteZoning,
      disabledZoningTypes,
      setDisabledZoningTypes,
    }),
    [
      activeSection,
      setActiveSection,
      sectionData,
      setSectionData,
      layerStates,
      toggleLayer,
      setLayerOpacity,
      getActiveSectionLayers,
      hasActiveSectionLayers,
      onMarkerClick,
      onMarkerHover,
      // setOnMarkerClick and setOnMarkerHover are stable setters, don't need to be in deps
      selectedZoningType,
      setSelectedZoningType,
      isSatelliteZoning,
      toggleSatelliteZoning,
      disabledZoningTypes,
      // setDisabledZoningTypes is a stable setter, don't need to be in deps
    ]
  )

  return (
    <MapSectionContext.Provider value={value}>
      {children}
    </MapSectionContext.Provider>
  )
}

/**
 * Hook to use Map Section Context
 */
export const useMapSection = (): MapSectionContextValue => {
  const context = useContext(MapSectionContext)
  if (!context) {
    throw new Error('useMapSection must be used within MapSectionProvider')
  }
  return context
}

/**
 * Optional hook to use Map Section Context
 * Returns undefined if not within provider (for components that can work standalone)
 */
export const useMapSectionOptional = (): MapSectionContextValue | undefined => {
  return useContext(MapSectionContext)
}
